import React from 'react';
import PropTypes from 'prop-types';
import { errorLog } from '@bitComponents/utils/logUtils';
import { setUpPropsByDevice } from '@bitComponents/utils/common';
import './price.scss';
import TextInfo, {
  TEXT_COLOR,
  TEXT_SIZE,
  TEXT_DISPLAY,
  TEXT_ALIGN,
  TEXT_KIND,
  TEXT_FONT_WEIGHT,
  TEXT_BORDER,
  TEXT_OPACITY,
  TEXT_DECORATION,
  TEXT_WRAP,
  TEXT_HEADING_LEVEL,
  TEXT_SEGMENT,
} from '@gcloud-npm/components.text';

export const parsePrice = (
  number,
  decimals = 2,
  decimalChar = ',',
  thousandChar = '.',
) => {
  let res = number;
  res = parseFloat(res).toFixed(decimals);
  res = res.replace('.', decimalChar);
  const splitNum = res.split(decimalChar);
  splitNum[0] = splitNum[0].replace(/\B(?=(\d{3})+(?!\d))/g, thousandChar);
  if (splitNum[1] !== '00') {
    res = splitNum.join(decimalChar);
  } else {
    res = splitNum[0];
  }
  return res;
};

export const convertToOptions = (optionsObject) => {
  const keys = Object.keys(optionsObject);
  return keys.map((key) => {
    return optionsObject[key];
  });
};

export {
  TEXT_ALIGN, TEXT_BORDER, TEXT_COLOR, TEXT_OPACITY, TEXT_DECORATION, TEXT_DISPLAY, TEXT_WRAP, TEXT_FONT_WEIGHT, TEXT_HEADING_LEVEL, TEXT_KIND, TEXT_SEGMENT, TEXT_SIZE,
};

/**
 * Stateless component to draw a price
 * @param {string||number} amount Price amount
 * @param {string} unity it is currency: €, or for example: €/mes
 * @param {string} color text color: allows: PRICE_COLOR .
 * @param {string} decoration extra decoration for component allows: PRICE_DECORATION
 * @param {string} size text fontsize allows: PRICE_SIZE
 * @param {bool} withSpace space between amount and unity
 * @param {string} opacity text opacity
 * @param {string} fontType text fontsize allows: FONT_TYPE
 * @returns {JSX} Component JSX.
 */

const Price = (props) => {
  const myProps = setUpPropsByDevice(props);

  const {
    amount,
    unity,
    size,
    color,
    decoration,
    withSpace,
    fontType,
  } = myProps;

  if ((!amount || !parseFloat(amount)) && amount !== 0) {
    if (isNaN(parseFloat(amount)) || parseFloat(amount) < 0) {
      errorLog(
        '[Price]: amount must be a valid input (number>=0 including string format with point before decimals)',
      );
      return null;
    }
  }

  const finalAmount = parsePrice(amount);
  const AmountUnityLabel = withSpace
    ? `${finalAmount} ${unity}`
    : `${finalAmount}${unity}`;

  return (
    <TextInfo
      className
      text={AmountUnityLabel}
      color={color}
      size={size}
      decoration={decoration}
      fontType={fontType}
    />
  );
};

Price.propTypes = {
  /**
   * {
   *  "info": "Establece la cantidad a mostrar en el componente",
   *  "kind": "both",
   *  "beautifulName": "Cantidad"
   * }
   */
  amount: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  /**
   * {
   *  "info": "Establece la unidad con la que mostrar la cantidad en el componente",
   *  "kind": "both",
   *  "beautifulName": "Unidad"
   * }
   */
  unity: PropTypes.string,
  /**
   * {
   *  "info": "Define el color del componente en el array PRICE_COLOR",
   *  "kind": "both",
   *  "beautifulName": "Color"
   * }
   */
  color: PropTypes.oneOf(convertToOptions(TEXT_COLOR)),
  /**
   * {
   *  "info": "Establece el tamaño del texto del componente: regular(24px) o small(16px)",
   *  "kind": "both",
   *  "beautifulName": "Tamaño texto"
   * }
   */
  size: PropTypes.oneOf(convertToOptions(TEXT_SIZE)),
  /**
   * {
   *  "info": "Establece la decoración del texto del componente: none, line_through, underline",
   *  "kind": "both",
   *  "beautifulName": "Decoración texto"
   * }
   */
  decoration: PropTypes.oneOf(convertToOptions(TEXT_DECORATION)),
  /**
   * {
   *  "info": "Establece si existe un espacio entre la cantidad y la unidad",
   *  "kind": "both",
   *  "beautifulName": "Espacio entre la cantidad y la unidad"
   * }
   */
  withSpace: PropTypes.bool,
  /**
   * {
   *  "info": "Define la fuente del componente según los tipos definidos en el array FONT_TYPE",
   *  "kind": "both",
   *  "beautifulName": "Fuente del texto"
   * }
   */
  fontType: PropTypes.oneOf(convertToOptions(TEXT_FONT_WEIGHT)),
};
Price.defaultProps = {
  unity: '€/mes',
  size: TEXT_SIZE.REGULAR,
  color: TEXT_COLOR.ORANGE,
  decoration: TEXT_DECORATION.NONE,
  withSpace: true,
};

export default Price;
